import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtMoney2, fmtPct, GRADE_BADGE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Gavel } from "lucide-react";

const FREQ_LABEL = { weekly: "Weekly", biweekly: "Bi-Weekly", monthly: "Monthly" };

export default function OriginationDetails() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [bids, setBids] = useState([]);

  // Bid form state
  const [rate, setRate] = useState(20);
  const [freq, setFreq] = useState("monthly");
  const [term, setTerm] = useState(48);
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/loan-requests/${id}`).then((r) => setReq(r.data)).catch(() => navigate("/originations"));
    if (user) api.get(`/loan-requests/${id}/bids`).then((r) => setBids(r.data.items || [])).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [id, user]);

  // Live preview of computed payment
  useEffect(() => {
    if (!req) return;
    const id2 = setTimeout(async () => {
      try {
        const r = await api.post("/loan-bids/preview", {
          loan_amount: req.loan_amount,
          proposed_interest_rate: rate,
          proposed_term: term,
          proposed_payment_frequency: freq,
        });
        setPreview(r.data);
      } catch {}
    }, 200);
    return () => clearTimeout(id2);
  }, [req, rate, freq, term]);

  const termHelp = useMemo(() => {
    const yrs = freq === "weekly" ? term / 52 : freq === "biweekly" ? term / 26 : term / 12;
    return `${term} ${FREQ_LABEL[freq].toLowerCase()} payments · ~${yrs.toFixed(1)} years`;
  }, [term, freq]);

  const submit = async () => {
    if (!user) { navigate("/login"); return; }
    if (user.role === "admin") { toast.error("Admins cannot place bids"); return; }
    if (rate <= 0 || rate > 28) { toast.error("Rate must be between 0 and 28%"); return; }
    setBusy(true);
    try {
      await api.post(`/loan-requests/${id}/bids`, {
        proposed_interest_rate: Number(rate),
        proposed_term: Number(term),
        proposed_payment_frequency: freq,
        message,
      });
      toast.success("Bid submitted");
      setMessage("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit bid");
    } finally { setBusy(false); }
  };

  if (!req) return <div className="mx-auto max-w-7xl px-4 py-16 text-muted-foreground">Loading…</div>;
  const { vehicle: v, buyer_profile: b } = req;
  const ltv = req.total_amount ? ((req.loan_amount / req.total_amount) * 100).toFixed(0) : "—";

  return (
    <div data-testid="page-origination-details" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/originations" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Originations
      </Link>

      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10">
        <div>
          <div className="border border-border aspect-[16/10] overflow-hidden bg-muted">
            <img src={v.photo} alt="" className="w-full h-full object-cover" />
          </div>

          <div className="mt-8 border-t border-border pt-6">
            <div className="font-overline mb-3">Loan Summary</div>
            <div className="border border-border divide-y divide-border">
              <Row label="Loan Amount (financed)" value={fmtMoney(req.loan_amount)} highlight />
              <Row label="Total Contract Value" value={fmtMoney(req.total_amount)} />
              <Row label="Loan-to-Value (LTV)" value={typeof ltv === "string" ? ltv : `${ltv}%`} />
              <Row label="Max APR Allowed" value={fmtPct(req.max_apr)} />
              <Row label="Status" value={req.status.toUpperCase()} />
            </div>
          </div>

          <div className="mt-8 border-t border-border pt-6">
            <div className="font-overline mb-3">Vehicle</div>
            <div className="border border-border divide-y divide-border">
              <Row label="Year / Make / Model" value={`${v.year} ${v.make} ${v.model}`} />
              <Row label="Mileage" value={`${(v.mileage || 0).toLocaleString()} mi`} />
              <Row label="State" value={v.state || b.state} />
            </div>
          </div>

          <div className="mt-8 border-t border-border pt-6">
            <div className="font-overline mb-3">Borrower Risk Profile (anonymized)</div>
            <div className="border border-border divide-y divide-border">
              <Row label="Credit Tier" value={<span className={`px-2 py-0.5 text-xs font-mono-data ${GRADE_BADGE[b.credit_tier]}`}>Tier {b.credit_tier}</span>} />
              <Row label="Monthly Income" value={b.monthly_income_range || "—"} />
              <Row label="DTI" value={b.dti_ratio ? `${b.dti_ratio}%` : "—"} />
              <Row label="Employment" value={(b.employment_type || "—").replace("_", " ")} />
              <Row label="Time on Job" value={b.time_on_job_months ? `${b.time_on_job_months} mo` : "—"} />
              <Row label="Prior Repos" value={b.prior_repos ?? "—"} />
            </div>
          </div>

          {req.description && (
            <div className="mt-8 border-t border-border pt-6">
              <div className="font-overline mb-3">Description</div>
              <p className="text-muted-foreground leading-relaxed text-sm">{req.description}</p>
            </div>
          )}
        </div>

        <div className="space-y-6" id="bid">
          {req.status === "open" ? (
            <div className="border border-border p-6 space-y-5">
              <div>
                <div className="font-overline text-accent">Place Your Bid</div>
                <h2 className="font-display text-2xl mt-1">Propose Your Terms</h2>
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <Label className="font-overline">Interest Rate (APR)</Label>
                  <span className="font-mono-data text-primary text-2xl" data-testid="bid-rate-display">{fmtPct(rate)}</span>
                </div>
                <Slider min={1} max={28} step={0.25} value={[rate]}
                  onValueChange={([v2]) => setRate(v2)} data-testid="bid-rate-slider" />
                <div className="flex justify-between text-[10px] font-mono-data text-muted-foreground mt-2">
                  <span>1%</span><span>14%</span><span>28% MAX</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-overline mb-2 block">Payment Frequency</Label>
                  <Select value={freq} onValueChange={setFreq}>
                    <SelectTrigger className="rounded-none" data-testid="bid-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-overline mb-2 block">Term ({FREQ_LABEL[freq]} payments)</Label>
                  <Input type="number" min={1} value={term}
                    onChange={(e) => setTerm(Math.max(1, Number(e.target.value) || 1))}
                    className="rounded-none font-mono-data" data-testid="bid-term" />
                </div>
              </div>
              <div className="text-xs text-muted-foreground font-mono-data">{termHelp}</div>

              {preview && (
                <div className="border border-border bg-muted/30 p-4 grid grid-cols-2 gap-4" data-testid="bid-preview">
                  <div>
                    <div className="font-overline text-muted-foreground">{FREQ_LABEL[freq]} Payment</div>
                    <div className="font-mono-data text-2xl text-primary">{fmtMoney2(preview.payment_amount)}</div>
                  </div>
                  <div>
                    <div className="font-overline text-muted-foreground">Total Payments</div>
                    <div className="font-mono-data text-2xl">{fmtMoney(preview.total_payments)}</div>
                  </div>
                </div>
              )}

              <div>
                <Label className="font-overline mb-2 block">Message (optional)</Label>
                <Textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)}
                  className="rounded-none" data-testid="bid-message" />
              </div>

              <Button onClick={submit} disabled={busy}
                className="w-full rounded-none uppercase tracking-wider h-12" data-testid="bid-submit">
                <Gavel className="w-4 h-4 mr-2" />{busy ? "Submitting…" : "Submit Bid"}
              </Button>
              <p className="text-[11px] text-muted-foreground">
                If accepted, the loan is originated immediately and the note is assigned to you.
              </p>
            </div>
          ) : (
            <div className="border border-border p-6">
              <div className="font-overline text-muted-foreground">Bidding Closed</div>
              <h2 className="font-display text-xl mt-1">This request is {req.status}.</h2>
            </div>
          )}

          {user && bids.length > 0 && (
            <div className="border border-border" data-testid="bid-list">
              <div className="px-5 py-3 border-b border-border font-overline">
                {user.role === "admin" ? `All Bids (${bids.length})` : `Your Bids (${bids.length})`}
              </div>
              <div className="divide-y divide-border">
                {bids.map((bd) => (
                  <BidRow key={bd.bid_id} bid={bd} isAdmin={user.role === "admin"} onChanged={load} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BidRow({ bid, isAdmin, onChanged }) {
  const accept = async () => {
    if (!window.confirm("Accept this bid? This will originate the loan and assign the note to the bidder.")) return;
    try { await api.post(`/loan-bids/${bid.bid_id}/accept`); toast.success("Bid accepted — loan originated"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reject = async () => {
    try { await api.post(`/loan-bids/${bid.bid_id}/reject`); toast.success("Bid rejected"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="px-5 py-3 grid grid-cols-2 md:grid-cols-5 gap-3 items-center" data-testid={`bid-row-${bid.bid_id}`}>
      <div>
        <div className="font-overline text-muted-foreground">Rate</div>
        <div className="font-mono-data text-lg text-primary">{fmtPct(bid.proposed_interest_rate)}</div>
      </div>
      <div>
        <div className="font-overline text-muted-foreground">Term</div>
        <div className="font-mono-data text-sm">{bid.proposed_term} {bid.proposed_payment_frequency}</div>
      </div>
      <div>
        <div className="font-overline text-muted-foreground">Payment</div>
        <div className="font-mono-data text-sm">{fmtMoney2(bid.computed_payment_amount)}</div>
      </div>
      <div>
        <div className="font-overline text-muted-foreground">Status</div>
        <div className="font-mono-data text-xs uppercase">{bid.status}</div>
      </div>
      <div className="text-right">
        {isAdmin && bid.status === "pending" && (
          <div className="flex gap-1 justify-end">
            <Button size="sm" className="rounded-none text-xs h-8" onClick={accept} data-testid={`accept-bid-${bid.bid_id}`}>Accept</Button>
            <Button size="sm" variant="outline" className="rounded-none text-xs h-8" onClick={reject} data-testid={`reject-bid-${bid.bid_id}`}>Reject</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between px-5 py-3">
      <div className="font-overline text-muted-foreground">{label}</div>
      <div className={`font-mono-data ${highlight ? "text-primary text-lg" : ""}`}>{value}</div>
    </div>
  );
}
